// src/services/meetingCategoriesService.js
// Servicio para gestionar categorías de reuniones compartidas entre ambos CRM

const STORAGE_KEY = 'crm_meeting_categories';

const DEFAULT_CATEGORIES = [
  { value: 'ACERCAMIENTO', label: 'Acercamiento' },
  { value: 'VINCULACION',  label: 'Vinculación' },
  { value: 'PROYECTO',     label: 'Proyecto' },
  { value: 'OTRA',         label: 'Otra' },
];

/**
 * Obtiene todas las categorías (predeterminadas + personalizadas)
 * Si hay categorías guardadas en localStorage, las usa; si no, devuelve las predeterminadas
 */
export const getCategories = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Ordenar DEFAULT_CATEGORIES alfabéticamente, pero mantener OTRA al final
      const otras = DEFAULT_CATEGORIES.filter(c => c.value === 'OTRA');
      const otrasSinOtra = DEFAULT_CATEGORIES.filter(c => c.value !== 'OTRA');
      return [...otrasSinOtra.sort((a, b) => a.label.localeCompare(b.label)), ...otras];
    }
    
    const saved = JSON.parse(stored);
    if (!Array.isArray(saved)) {
      // Ordenar DEFAULT_CATEGORIES alfabéticamente, pero mantener OTRA al final
      const otras = DEFAULT_CATEGORIES.filter(c => c.value === 'OTRA');
      const otrasSinOtra = DEFAULT_CATEGORIES.filter(c => c.value !== 'OTRA');
      return [...otrasSinOtra.sort((a, b) => a.label.localeCompare(b.label)), ...otras];
    }
    
    // Si hay categorías guardadas, usarlas (pueden incluir o no las predeterminadas)
    if (saved.length > 0) {
      // Separar OTRA del resto, ordenar alfabéticamente y poner OTRA al final
      const otras = saved.filter(c => c.value === 'OTRA');
      const otrasSinOtra = saved.filter(c => c.value !== 'OTRA');
      return [...otrasSinOtra.sort((a, b) => a.label.localeCompare(b.label)), ...otras];
    }
    
    // Ordenar DEFAULT_CATEGORIES alfabéticamente, pero mantener OTRA al final
    const otras = DEFAULT_CATEGORIES.filter(c => c.value === 'OTRA');
    const otrasSinOtra = DEFAULT_CATEGORIES.filter(c => c.value !== 'OTRA');
    return [...otrasSinOtra.sort((a, b) => a.label.localeCompare(b.label)), ...otras];
  } catch {
    // Ordenar DEFAULT_CATEGORIES alfabéticamente, pero mantener OTRA al final
    const otras = DEFAULT_CATEGORIES.filter(c => c.value === 'OTRA');
    const otrasSinOtra = DEFAULT_CATEGORIES.filter(c => c.value !== 'OTRA');
    return [...otrasSinOtra.sort((a, b) => a.label.localeCompare(b.label)), ...otras];
  }
};

/**
 * Agrega una nueva categoría
 */
export const addCategory = (name) => {
  const trimmed = String(name || '').trim();
  if (!trimmed) throw new Error('El nombre de la categoría no puede estar vacío');
  
  const upperValue = trimmed.toUpperCase().replace(/\s+/g, '_');
  const label = trimmed;
  
  // Verificar que no exista ya
  const all = getCategories();
  if (all.some(c => c.value === upperValue || c.label.toLowerCase() === trimmed.toLowerCase())) {
    throw new Error('Esta categoría ya existe');
  }
  
  // Obtener todas las categorías actuales
  let current = getCategories();
  
  // Agregar nueva categoría
  current.push({ value: upperValue, label });
  
  // Guardar todas las categorías en localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  
  return { value: upperValue, label };
};

/**
 * Elimina una categoría (puede ser predeterminada o personalizada)
 * Previene eliminar la última categoría disponible
 * Nota: Las reuniones que usan esta categoría quedarán sin categoría asignada
 */
export const deleteCategory = (categoryValue) => {
  // Obtener todas las categorías actuales
  const all = getCategories();
  
  // Validar que no se intente eliminar la última categoría
  if (all.length <= 1) {
    throw new Error('No se puede eliminar la última categoría. Crea una nueva categoría antes de eliminar esta. Las reuniones que usan esta categoría quedarán sin categoría asignada.');
  }
  
  // Filtrar la categoría a eliminar
  const filtered = all.filter(c => c.value !== categoryValue);
  
  // Validar nuevamente después de filtrar (por si acaso)
  if (filtered.length === 0) {
    throw new Error('No se puede eliminar la última categoría. Crea una nueva categoría antes de eliminar esta. Las reuniones que usan esta categoría quedarán sin categoría asignada.');
  }
  
  // Guardar en localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
};

/**
 * Obtiene todas las categorías (ahora todas pueden ser eliminadas)
 * Esta función es un alias de getCategories para mantener compatibilidad
 */
export const getCustomCategories = () => {
  return getCategories();
};

